import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Bell, Check, CheckCheck, AlertTriangle, Wrench, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function NotificationBell() {
    const { token } = useAuth();
    const [count, setCount] = useState(0);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const headers = { Authorization: `Bearer ${token}` };

    const fetchCount = useCallback(async () => {
        if (!token) return;
        try {
            const res = await axios.get(`${API}/notifications/count`, { headers });
            setCount(res.data.unread);
        } catch {}
    }, [token]);

    useEffect(() => {
        fetchCount();
        const interval = setInterval(fetchCount, 15000);
        window.addEventListener('notifications-changed', fetchCount);
        return () => { clearInterval(interval); window.removeEventListener('notifications-changed', fetchCount); };
    }, [fetchCount]);

    const fetchNotifications = async () => {
        try {
            const res = await axios.get(`${API}/notifications`, { headers });
            setNotifications(res.data);
        } catch {}
    };

    const togglePanel = () => {
        if (!open) fetchNotifications();
        setOpen(!open);
    };

    const markRead = async (id) => {
        await axios.patch(`${API}/notifications/${id}/read`, null, { headers });
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
        setCount(prev => Math.max(0, prev - 1));
    };

    const markAllRead = async () => {
        await axios.patch(`${API}/notifications/read-all`, null, { headers });
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        setCount(0);
    };

    const severityColor = (sev) => {
        if (sev === 'critical') return 'text-red-400';
        if (sev === 'major') return 'text-amber-400';
        return 'text-blue-400';
    };

    const typeIcon = (type) => {
        if (type === 'captain_report') return <AlertTriangle className="w-4 h-4" />;
        if (type === 'report_update') return <Wrench className="w-4 h-4" />;
        return <FileText className="w-4 h-4" />;
    };

    const timeAgo = (dateStr) => {
        const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
        if (diff < 60) return 'ahora';
        if (diff < 3600) return `${Math.floor(diff / 60)}m`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
        return `${Math.floor(diff / 86400)}d`;
    };

    return (
        <div className="relative" data-testid="notification-bell">
            <Button variant="ghost" size="icon" onClick={togglePanel} className="relative">
                <Bell className="w-5 h-5" />
                {count > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="notification-count">
                        {count > 9 ? '9+' : count}
                    </span>
                )}
            </Button>

            {open && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div className="absolute left-0 top-full mt-2 w-96 max-h-[480px] bg-card border border-border rounded-lg shadow-2xl z-50 overflow-hidden" data-testid="notification-panel">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                            <h3 className="font-semibold text-sm">Notificaciones</h3>
                            {count > 0 && (
                                <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs h-7">
                                    <CheckCheck className="w-3 h-3 mr-1" />Marcar todas
                                </Button>
                            )}
                        </div>
                        <div className="overflow-y-auto max-h-[400px]">
                            {notifications.length === 0 ? (
                                <p className="p-6 text-center text-muted-foreground text-sm">Sin notificaciones</p>
                            ) : notifications.map(n => (
                                <div
                                    key={n.id}
                                    className={`px-4 py-3 border-b border-border/50 hover:bg-muted/30 cursor-pointer transition-colors ${!n.read ? 'bg-muted/20' : ''}`}
                                    onClick={() => { if (!n.read) markRead(n.id); if (n.type === 'op_message') { setOpen(false); navigate('/mensajes'); } }}
                                    data-testid={`notification-item-${n.id}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`mt-0.5 ${severityColor(n.severity)}`}>
                                            {typeIcon(n.type)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className={`text-xs font-semibold truncate ${!n.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                                                    {n.title}
                                                </p>
                                                {!n.read && <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                                            <p className="text-[10px] text-muted-foreground/60 mt-1">{timeAgo(n.created_at)}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button className="w-full px-4 py-2 text-xs text-primary hover:bg-muted/30 border-t border-border" onClick={() => { setOpen(false); navigate('/notifications'); }} data-testid="notification-see-all">
                            Ver todos los avisos
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
